import { api } from './client';

export async function getAdminStats() {
  return api.get('/admin/stats');
}

export async function getAdminUsers({ page = 1, limit = 25, sort = 'created_at', order = 'desc', search = '' } = {}) {
  const params = new URLSearchParams({ page, limit, sort, order });
  if (search) params.set('search', search);
  return api.get(`/admin/users?${params}`);
}

export async function getAdminUser(id) {
  return api.get(`/admin/users/${id}`);
}

export async function getAdminStories({ page = 1, limit = 25 } = {}) {
  return api.get(`/admin/stories?page=${page}&limit=${limit}`);
}

export async function getAdminCharacters({ page = 1, limit = 50, search = '', hsk_level = '' } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set('search', search);
  if (hsk_level) params.set('hsk_level', hsk_level);
  return api.get(`/admin/characters?${params}`);
}

export async function adminSetAdmin(userId, isAdmin) {
  return api.patch(`/admin/users/${userId}/set-admin`, { is_admin: isAdmin });
}

export async function adminUpdateSubscription(userId, action, options = {}) {
  return api.patch(`/admin/users/${userId}/subscription`, { action, ...options });
}

export async function adminResetDailyLimit(userId) {
  return api.post(`/admin/users/${userId}/reset-daily-limit`, {});
}

export async function adminDeleteUser(userId) {
  return api.delete(`/admin/users/${userId}`);
}

export async function adminSetLearnerLevel(learnerId, level) {
  return api.post(`/admin/learners/${learnerId}/set-level`, { level });
}

// ─── Library Admin ───────────────────────────────────────────

export async function getAdminSeries() {
  return api.get('/admin/series');
}

export async function createAdminSeries(payload) {
  return api.post('/admin/series', payload);
}

export async function updateAdminSeries(id, payload) {
  return api.patch(`/admin/series/${id}`, payload);
}

export async function getAdminSeriesTemplates(seriesId) {
  return api.get(`/admin/story-templates${seriesId ? `?series_id=${seriesId}` : ''}`);
}

export async function updateAdminTemplate(id, payload) {
  return api.patch(`/admin/story-templates/${id}`, payload);
}

export async function publishAdminTemplate(id, active = true) {
  return api.post(`/admin/story-templates/${id}/publish`, { active });
}

/**
 * Upload a new story template — multipart/form-data.
 * Cannot use api.post() because that sets Content-Type: application/json.
 * Uses raw fetch with auth token instead.
 */
export async function uploadAdminTemplate(formData) {
  const token = localStorage.getItem('access_token');
  const API_URL = import.meta.env.VITE_API_URL ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:3000'
      : 'https://linguapals-backend.onrender.com');

  const res = await fetch(`${API_URL}/admin/story-templates`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Upload failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Update an existing story template — multipart/form-data.
 * Supports optional cover image and/or story file replacement alongside metadata fields.
 * Cannot use api.patch() because that sets Content-Type: application/json.
 */
export async function patchAdminTemplateWithFiles(id, formData) {
  const token = localStorage.getItem('access_token');
  const API_URL = import.meta.env.VITE_API_URL ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:3000'
      : 'https://linguapals-backend.onrender.com');

  const res = await fetch(`${API_URL}/admin/story-templates/${id}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Update failed: ${res.status}`);
  }

  return res.json();
}
